import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  Book,
  PartyPopper,
  Star,
  Repeat,
  Plus,
  Edit2,
  Trash2,
  Clock,
  Users,
  Sparkles,
} from "lucide-react";
import { TemplateEditorModal } from "@/components/TemplateEditorModal";

interface StreamTemplate {
  id: string;
  name: string;
  description: string;
  category: 'worship' | 'study' | 'event' | 'special' | 'recurring';
  titleFormat: string;
  defaultBlurb: string;
  defaultTime: string;
  defaultDuration: number;
  isRecurring: boolean;
  recurrencePattern?: string;
  recurrenceDayOfWeek?: number;
  defaultPlatforms: string[];
  defaultTags: string;
  isSystem: boolean;
  usageCount: number;
}

const categoryIcons = {
  worship: <Calendar className="w-5 h-5" />,
  study: <Book className="w-5 h-5" />,
  event: <PartyPopper className="w-5 h-5" />,
  special: <Star className="w-5 h-5" />,
  recurring: <Repeat className="w-5 h-5" />,
};

const categoryColors = {
  worship: 'bg-blue-500/10 text-blue-700 border-blue-200',
  study: 'bg-green-500/10 text-green-700 border-green-200',
  event: 'bg-purple-500/10 text-purple-700 border-purple-200',
  special: 'bg-yellow-500/10 text-yellow-700 border-yellow-200',
  recurring: 'bg-orange-500/10 text-orange-700 border-orange-200',
};

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Templates() {
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<StreamTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery<StreamTemplate[]>({
    queryKey: ['/api/stream-templates'],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/stream-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/stream-templates'] });
      toast({
        title: 'Template deleted',
        description: 'The template has been removed successfully.',
      });
      setDeleteConfirm(null);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete template. System templates cannot be deleted.',
        variant: 'destructive',
      });
      setDeleteConfirm(null);
    },
  });

  const handleEdit = (template: StreamTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteMutation.mutate(deleteConfirm);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const groupedTemplates = templates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, StreamTemplate[]>);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Stream Templates</h1>
          <p className="text-muted-foreground mt-2">
            Create and manage reusable templates for quick stream scheduling
          </p>
        </div>
        <Button onClick={handleCreateNew} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No templates yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first template to speed up stream scheduling
            </p>
            <Button onClick={handleCreateNew}>
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
            <div key={category}>
              <h2 className="text-xl font-semibold mb-4 capitalize flex items-center gap-2">
                {categoryIcons[category as keyof typeof categoryIcons]}
                {category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryTemplates.map((template) => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className={`p-2 rounded-lg ${categoryColors[template.category]} mb-2`}>
                          {categoryIcons[template.category]}
                        </div>
                        {template.isSystem && (
                          <Badge variant="secondary" className="text-xs">
                            <Sparkles className="w-3 h-3 mr-1" />
                            System
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      {template.description && (
                        <CardDescription>{template.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="text-sm text-muted-foreground">
                        <strong>Title:</strong> {template.titleFormat}
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {template.isRecurring && (
                          <Badge variant="outline" className="text-xs">
                            <Repeat className="w-3 h-3 mr-1" />
                            {template.recurrencePattern} on {daysOfWeek[template.recurrenceDayOfWeek || 0]}
                          </Badge>
                        )}
                        {template.defaultTime && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {template.defaultTime}
                          </Badge>
                        )}
                        {template.defaultDuration && (
                          <Badge variant="outline" className="text-xs">
                            {template.defaultDuration} min
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex gap-1">
                          {template.defaultPlatforms?.map((platform) => (
                            <Badge key={platform} variant="secondary" className="text-xs">
                              {platform}
                            </Badge>
                          ))}
                        </div>
                        {template.usageCount > 0 && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Users className="w-3 h-3" />
                            {template.usageCount}
                          </div>
                        )}
                      </div>

                      {!template.isSystem && (
                        <div className="flex gap-2 pt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleEdit(template)}
                          >
                            <Edit2 className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleDelete(template.id)}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this template. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
